import React, { useState, useEffect, useCallback } from 'react';
import { Users, Gift, Copy, Share2, Trophy, Check, UserPlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FriendsPage() {
  const [inviteData, setInviteData] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('invite');

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const [codeRes, statsRes, lbRes] = await Promise.all([
        fetch(`${API_URL}/api/friends/my-code`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/friends/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/friends/leaderboard`)
      ]);

      if (codeRes.ok) setInviteData(await codeRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (lbRes.ok) {
        const lb = await lbRes.json();
        setLeaderboard(lb.leaderboard || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyCode = () => {
    if (inviteData?.invite_code) {
      navigator.clipboard.writeText(inviteData.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = async () => {
    if (inviteData?.invite_link && navigator.share) {
      try {
        await navigator.share({
          title: 'OddsCortex - KI Sport Tipps',
          text: 'Melde dich bei OddsCortex an und erhalte 50 XP Bonus!',
          url: inviteData.invite_link,
        });
      } catch (err) {
        copyCode();
      }
    } else {
      copyCode();
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/friends/send-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: inviteEmail })
      });
      if (res.ok) {
        setInviteEmail('');
        fetchData();
        alert('Einladung gesendet!');
      } else {
        const err = await res.json();
        alert(err.detail || 'Fehler');
      }
    } catch (err) {
      alert('Netzwerkfehler');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 md:p-8" data-testid="friends-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-8 h-8 text-teal-400" />
          <h1 className="text-3xl font-bold">Freunde einladen</h1>
        </div>
        <p className="text-gray-400">Lade Freunde ein und verdiene Belohnungen!</p>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex gap-2">
          {['invite', 'stats', 'leaderboard'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeTab === tab 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {tab === 'invite' ? 'Einladen' : tab === 'stats' ? 'Statistiken' : 'Rangliste'}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* INVITE TAB */}
        {activeTab === 'invite' && (
          <div className="space-y-6">
            {/* Rewards Info */}
            <div className="bg-gradient-to-r from-teal-900/30 to-purple-900/30 rounded-xl p-6 border border-teal-500/30">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Gift className="w-6 h-6 text-yellow-400" />
                Belohnungen
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-teal-400">+100 XP</div>
                  <div className="text-sm text-gray-400">Fuer dich pro Einladung</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400">+50 XP</div>
                  <div className="text-sm text-gray-400">Fuer deinen Freund</div>
                </div>
              </div>
              <div className="mt-4 text-center text-sm text-gray-400">
                +3 Bonus-Tipps pro erfolgreicher Einladung!
              </div>
            </div>

            {/* Invite Code */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="font-semibold mb-4">Dein Einladungscode</h3>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-800 rounded-lg p-4 font-mono text-xl text-center tracking-wider">
                  {inviteData?.invite_code || '---'}
                </div>
                <button
                  onClick={copyCode}
                  className="p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition"
                >
                  {copied ? <Check className="w-6 h-6 text-green-400" /> : <Copy className="w-6 h-6" />}
                </button>
                <button
                  onClick={shareLink}
                  className="p-4 bg-teal-500 hover:bg-teal-400 rounded-lg transition"
                >
                  <Share2 className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Email Invite */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Per E-Mail einladen
              </h3>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="freund@email.de"
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-500"
                />
                <button
                  onClick={sendInvite}
                  disabled={sending || !inviteEmail}
                  className="px-6 py-3 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-lg transition disabled:opacity-50"
                >
                  {sending ? '...' : 'Senden'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STATS TAB */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 text-center">
                <div className="text-2xl font-bold text-teal-400">{stats.successful_invites || 0}</div>
                <div className="text-sm text-gray-400">Erfolgreiche Einladungen</div>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 text-center">
                <div className="text-2xl font-bold text-yellow-400">{stats.pending_invites || 0}</div>
                <div className="text-sm text-gray-400">Ausstehend</div>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 text-center">
                <div className="text-2xl font-bold text-green-400">{stats.total_xp_earned || 0}</div>
                <div className="text-sm text-gray-400">XP verdient</div>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 text-center">
                <div className="text-2xl font-bold text-purple-400">{stats.total_invites || 0}</div>
                <div className="text-sm text-gray-400">Gesamt</div>
              </div>
            </div>

            {/* Invited users */}
            {stats.invited_users?.length > 0 && (
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 bg-gray-800">
                  <h3 className="font-semibold">Eingeladene Freunde</h3>
                </div>
                <div className="divide-y divide-gray-800">
                  {stats.invited_users.map((user, idx) => (
                    <div key={idx} className="p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{user.email}</div>
                        <div className="text-sm text-gray-500">{user.joined?.split('T')[0]}</div>
                      </div>
                      <div className="text-teal-400 font-semibold">{user.xp} XP</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* LEADERBOARD TAB */}
        {activeTab === 'leaderboard' && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 bg-gray-800 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <h3 className="font-semibold">Top Einlader</h3>
            </div>
            <div className="divide-y divide-gray-800">
              {leaderboard.map((entry, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-orange-600 text-white' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {entry.rank}
                    </div>
                    <div>
                      <div className="font-medium">{entry.email}</div>
                      <div className="text-sm text-gray-500">{entry.invites} Einladungen</div>
                    </div>
                  </div>
                  <div className="text-teal-400 font-semibold">+{entry.xp_earned} XP</div>
                </div>
              ))}
              {leaderboard.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  Noch keine Eintraege. Sei der Erste!
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
